/**
 * Admin panel: Free-Score PDF Leads.
 *
 * Every visitor who uploads their LinkedIn PDF via /api/free-score/from-pdf
 * lands here with the fields extracted from the PDF — name, headline,
 * industry, current title, and the LinkedIn public URL when the PDF still
 * contains it. Ops uses this table for warm outreach: the person has
 * self-identified, run an audit, and left enough context to write a
 * meaningful follow-up.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { RefreshCw, Loader2, FileText, ExternalLink, Download } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = typeof localStorage !== 'undefined' ? localStorage.getItem('le_token') : null;
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function fmtDate(v) {
  if (!v) return '—';
  try { return format(new Date(v), 'MMM d, HH:mm'); } catch { return '—'; }
}

function toCsv(rows) {
  const cols = ['created_at', 'name', 'linkedin_url', 'headline', 'current_title', 'industry', 'score', 'confidence', 'status', 'objective', 'converted_to_signup'];
  const esc = (v) => {
    if (v == null) return '';
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  return [cols.join(','), ...rows.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\n');
}

export function FreeScorePdfLeadsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND}/api/admin/free-score-leads`, {
        headers: authHeaders(),
        params: { limit: 1000 },
        timeout: 20000,
      });
      setRows(res?.data?.items || []);
    } catch {
      toast.error('Could not load free-score PDF leads.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => (
      (r.name || '').toLowerCase().includes(needle)
      || (r.linkedin_url || '').toLowerCase().includes(needle)
      || (r.headline || '').toLowerCase().includes(needle)
      || (r.industry || '').toLowerCase().includes(needle)
    ));
  }, [rows, q]);

  const signedUpCount = filtered.filter((r) => r.converted_to_signup).length;

  const exportCsv = () => {
    if (!filtered.length) { toast.error('No rows to export.'); return; }
    const blob = new Blob([toCsv(filtered)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `blayzrr-free-score-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Card className="border-border mt-6" data-testid="admin-free-score-pdf-leads-card">
      <CardContent className="p-0">
        <div className="p-4 border-b border-border flex items-center gap-2 flex-wrap">
          <FileText size={18} className="text-primary" />
          <h2 className="font-semibold">Free-score PDF leads</h2>
          <Badge variant="secondary" className="ml-1" data-testid="admin-pdf-leads-count">{filtered.length}</Badge>
          {filtered.length > 0 && (
            <Badge
              variant="outline"
              className="text-[10px] border-emerald-400/50 text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10"
              data-testid="admin-pdf-leads-conversion"
            >
              {signedUpCount} signed up · {Math.round((signedUpCount / filtered.length) * 100)}%
            </Badge>
          )}
          <span className="text-xs text-muted-foreground hidden md:inline">
            Name + LinkedIn URL from every PDF-based free score submission.
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name / URL / headline…"
              className="h-8 w-48 text-xs"
              data-testid="admin-pdf-leads-search"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={exportCsv}
              disabled={loading || filtered.length === 0}
              className="h-8"
              data-testid="admin-pdf-leads-export"
            >
              <Download size={13} className="mr-1" /> CSV
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={load}
              disabled={loading}
              className="h-8"
              data-testid="admin-pdf-leads-refresh"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            </Button>
          </div>
        </div>

        {filtered.length === 0 && !loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No PDF-based free-score submissions yet. Rows appear as soon as a visitor uploads a LinkedIn PDF on /free-score.
          </div>
        ) : (
          <div className="max-h-[500px] overflow-auto">
            <Table className="min-w-[1080px]">
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>LinkedIn URL</TableHead>
                  <TableHead>Headline</TableHead>
                  <TableHead>Industry</TableHead>
                  <TableHead className="text-right">Score</TableHead>
                  <TableHead>Confidence</TableHead>
                  <TableHead>Stage · Objective</TableHead>
                  <TableHead>Signed up?</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((row) => (
                  <TableRow key={row.id} data-testid={`admin-pdf-lead-row-${row.id}`}>
                    <TableCell className="text-xs whitespace-nowrap">{fmtDate(row.created_at)}</TableCell>
                    <TableCell className="text-sm font-medium max-w-[180px] truncate" title={row.name}>{row.name || '—'}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {row.linkedin_url ? (
                        <a
                          href={row.linkedin_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary hover:underline text-xs inline-flex items-center gap-1 max-w-[220px] truncate"
                          title={row.linkedin_url}
                          data-testid={`admin-pdf-lead-url-${row.id}`}
                        >
                          {row.linkedin_url.replace(/^https?:\/\/(www\.)?/, '')}
                          <ExternalLink size={10} />
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs max-w-[260px] truncate" title={row.headline}>{row.headline || '—'}</TableCell>
                    <TableCell className="text-xs max-w-[140px] truncate" title={row.industry}>{row.industry || '—'}</TableCell>
                    <TableCell className="text-right text-sm font-mono-edge tabular-nums">{row.score != null ? row.score : '—'}</TableCell>
                    <TableCell className="text-xs capitalize">{row.confidence || '—'}</TableCell>
                    <TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">
                      {(row.status || '—')} · {(row.objective || '—').replace(/_/g, ' ')}
                    </TableCell>
                    <TableCell>
                      {row.converted_to_signup ? (
                        <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white text-[10px]">Signed up</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default FreeScorePdfLeadsPanel;
